import {ITraderApplication, ITradingFormField} from 'constants/interfaces';

type ITraderFieldOption = {
  title?: string;
  value?: string | number | null;
};

export type ITraderFieldLike = Partial<ITradingFormField> & {
  groupBy?: string | number;
  textTitle?: string;
  textType?: string;
  groupView?: string;
  checkBoxTitle?: string;
  checkBoxValue?: boolean;
  customGroupTitle?: string;
  title?: string;
  textInputTitle?: string;
  singleChoiceOptionTitle?: string;
  value?: unknown;
  textInputValue?: unknown;
  singleChoiceOptionValue?: unknown;
  multiChoiceOptionValues?: unknown;
  validationTypes?: string[] | string;
  textInputValidationTypes?: string[] | string;
  textInputValidationType?: string[] | string;
  options?: ITraderFieldOption[];
  singleChoiceOptionOptions?: ITraderFieldOption[];
  fields?: ITraderFieldLike[];
  customGroupFields?: ITraderFieldLike[];
  isOptional?: boolean;
};

export interface ITraderRelatedPartySummary {
  id: string;
  fullName: string;
  idNumber: string;
  phone: string;
  address: string;
  creditScore: string;
}

export interface ITraderBankingSummary {
  bankName: string;
  branchCode: string;
  accountType: string;
  accountNumber: string;
}

const COMPANY_TYPE_MAP: Record<string, string> = {
  '1': 'Private Company',
  '2': 'Sole Proprietor',
  '3': 'Close Corporation',
  '4': 'Trust',
  '5': 'Public Company',
  '6': 'Co-operative',
  '07': 'Pty Ltd',
  '23': 'Close Corporation',
};

function lshToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function lshToLower(value: unknown): string {
  return lshToText(value).toLowerCase();
}

function lshToArray<T>(value: T[] | T | undefined | null): T[] {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  return [value];
}

function lshGetNestedFields(field?: ITraderFieldLike): ITraderFieldLike[] {
  if (!field) return [];
  return [...(field.fields || []), ...(field.customGroupFields || [])].filter(
    Boolean,
  );
}

export function lshGetTraderFieldCurrentValue(
  field?: ITraderFieldLike,
): string {
  if (!field) return '';

  const rawValue =
    field.value ??
    field.textInputValue ??
    field.singleChoiceOptionValue ??
    field.multiChoiceOptionValues ??
    field.checkBoxValue;

  if (Array.isArray(rawValue)) {
    return rawValue.map(lshToText).filter(Boolean).join(', ');
  }

  return lshToText(rawValue);
}

export function lshGetTraderFieldLabel(field?: ITraderFieldLike): string {
  return lshToText(
    field?.title ||
      field?.textTitle ||
      field?.textInputTitle ||
      field?.singleChoiceOptionTitle ||
      field?.checkBoxTitle,
  );
}

function lshGetSingleChoiceOptionTitle(field?: ITraderFieldLike): string {
  if (!field) return '';

  const value = lshGetTraderFieldCurrentValue(field);
  const options = (
    field.options ||
    field.singleChoiceOptionOptions ||
    []
  ).filter(Boolean);
  const selected = options.find(
    (option) => lshToText(option?.value) === lshToText(value),
  );

  return lshToText(selected?.title || value);
}

export function lshGetTraderFieldValidations(
  field?: ITraderFieldLike,
): string[] {
  const validationValue =
    field?.validationTypes ||
    field?.textInputValidationTypes ||
    field?.textInputValidationType;

  return lshToArray(validationValue)
    .map((value) => lshToText(value))
    .filter(Boolean);
}

export function lshFlattenTraderFormFields(
  fields?: ITraderFieldLike[],
): ITraderFieldLike[] {
  const flattened: ITraderFieldLike[] = [];
  const queue = [...(fields || [])];

  while (queue.length > 0) {
    const field = queue.shift();
    if (!field) continue;

    flattened.push(field);
    queue.push(...lshGetNestedFields(field));
  }

  return flattened;
}

export function lshFlattenTraderApplicationFields(
  traderApplication?: ITraderApplication,
): ITraderFieldLike[] {
  if (!traderApplication?.forms) return [];

  return traderApplication.forms.flatMap((form) =>
    (form.sections || []).flatMap((section) =>
      lshFlattenTraderFormFields((section.fields || []) as ITraderFieldLike[]),
    ),
  );
}

function lshMatchesRegistrationField(field: ITraderFieldLike): boolean {
  const id = lshToLower(field.id);

  return (
    lshGetTraderFieldValidations(field).includes('ENTITY_REG_NUMBER') ||
    id ===
      'private_company_form_company_verification_company_registration_number' ||
    id.includes('registration_number')
  );
}

export function lshGetTraderRegistrationField(
  traderApplication?: ITraderApplication,
): ITraderFieldLike | undefined {
  const fields = lshFlattenTraderApplicationFields(traderApplication);

  return (
    fields.find((field) => lshMatchesRegistrationField(field)) || undefined
  );
}

export function lshExtractRegistrationNumberFromTraderApplication(
  traderApplication?: ITraderApplication,
): string {
  return lshGetTraderFieldCurrentValue(
    lshGetTraderRegistrationField(traderApplication),
  );
}

function lshFlattenPrivateCompanyFields(
  traderApplication?: ITraderApplication,
): ITraderFieldLike[] {
  const forms = traderApplication?.forms || [];
  const companyForms = forms.filter((form) =>
    lshToLower(form?.id).includes('private_company_form'),
  );
  const sourceForms = companyForms.length > 0 ? companyForms : forms;

  return sourceForms.flatMap((form) =>
    (form.sections || []).flatMap((section) =>
      lshFlattenTraderFormFields((section.fields || []) as ITraderFieldLike[]),
    ),
  );
}

function lshIsCompanyDetailsSection(section: any): boolean {
  const id = lshToLower(section?.id);
  const name = lshToLower(section?.name);

  if (id.includes('related_parties') || name.includes('related parties')) {
    return false;
  }

  return (
    id.includes('private_company_form_company_details') ||
    id.includes('company_details') ||
    name.includes('company details')
  );
}

export function lshCollectCompanyDetailsFieldsFromTraderApplication(
  traderApplication?: ITraderApplication,
): ITraderFieldLike[] {
  const forms = traderApplication?.forms || [];

  const companyDetailsSections = forms.flatMap((form) =>
    (form.sections || []).filter((section) =>
      lshIsCompanyDetailsSection(section),
    ),
  );

  if (companyDetailsSections.length > 0) {
    return companyDetailsSections.flatMap((section) =>
      lshFlattenTraderFormFields((section.fields || []) as ITraderFieldLike[]),
    );
  }

  return lshFlattenPrivateCompanyFields(traderApplication);
}

function lshFindPrivateCompanyField(
  traderApplication: ITraderApplication | undefined,
  matcher: (field: ITraderFieldLike) => boolean,
): ITraderFieldLike | undefined {
  const fields =
    lshCollectCompanyDetailsFieldsFromTraderApplication(traderApplication);
  return fields.find((field) => matcher(field));
}

export function lshExtractTradingNameFromTraderApplication(
  traderApplication?: ITraderApplication,
): string {
  const field = lshFindPrivateCompanyField(traderApplication, (candidate) => {
    const id = lshToLower(candidate.id);
    const label = lshToLower(lshGetTraderFieldLabel(candidate));

    if (
      id.includes('id_number') ||
      label.includes('id number') ||
      id.includes('registration_name') ||
      label.includes('registration name')
    ) {
      return false;
    }
    if (id.includes('certificate') || label.includes('certificate')) {
      return false;
    }

    return id.includes('trading_name') || label.includes('trading name');
  });

  return lshGetTraderFieldCurrentValue(field);
}

export function lshExtractVatNumberFromTraderApplication(
  traderApplication?: ITraderApplication,
): string {
  const field = lshFindPrivateCompanyField(traderApplication, (candidate) => {
    const id = lshToLower(candidate.id);
    const label = lshToLower(lshGetTraderFieldLabel(candidate));

    if (id.includes('certificate') || label.includes('certificate')) {
      return false;
    }
    if (id.includes('tax_pin') || label.includes('tax pin')) {
      return false;
    }

    return (
      id.includes('vat_number') ||
      id.includes('vat_no') ||
      (id.includes('vat') &&
        !id.includes('private_company_form_upload_docs')) ||
      label.includes('vat number')
    );
  });

  return lshGetTraderFieldCurrentValue(field);
}

export function lshExtractTaxNumberFromTraderApplication(
  traderApplication?: ITraderApplication,
): string {
  const field = lshFindPrivateCompanyField(traderApplication, (candidate) => {
    const id = lshToLower(candidate.id);
    const label = lshToLower(lshGetTraderFieldLabel(candidate));

    if (id.includes('pin') || label.includes('pin')) {
      return false;
    }
    if (id.includes('certificate') || label.includes('certificate')) {
      return false;
    }

    return (
      id.includes('tax_number') ||
      id.includes('tax_no') ||
      label.includes('tax number')
    );
  });

  return lshGetTraderFieldCurrentValue(field);
}

function lshBuildRelatedPartyAddress(parts: string[]): string {
  return parts.map(lshToText).filter(Boolean).join(', ');
}

function lshExtractRelatedPartyFromFields(
  fields: ITraderFieldLike[],
  fallbackTitle?: string,
): ITraderRelatedPartySummary | null {
  if (!fields || fields.length === 0) return null;

  let id = '';
  let fullName = '';
  let idNumber = '';
  let phone = '';
  let creditScore = '';
  const addressParts: string[] = [];

  fields.forEach((field) => {
    if (!field) return;

    if (String((field as any).__typename || '') === 'CustomGroup') {
      const nested = lshExtractRelatedPartyFromFields(
        lshGetNestedFields(field),
        lshToText(field.customGroupTitle) || fallbackTitle,
      );

      if (!nested) return;

      fullName = nested.fullName || fullName;
      idNumber = nested.idNumber || idNumber;
      phone = nested.phone || phone;
      creditScore = nested.creditScore || creditScore;
      if (nested.address) {
        addressParts.push(nested.address);
      }
      id = nested.id || id;
      return;
    }

    const label = lshToLower(lshGetTraderFieldLabel(field));
    const fieldId = lshToLower(field.id);
    const value = lshGetTraderFieldCurrentValue(field);
    const validations = lshGetTraderFieldValidations(field);

    if (validations.includes('ID_NUMBER')) {
      idNumber = value;
      id = lshToText(field.id || field.groupBy || id);
    } else if (validations.includes('PHONE')) {
      phone = value;
    } else if (validations.includes('ADDRESS')) {
      if (value) addressParts.push(value);
    } else if (
      fieldId.includes('credit_score') ||
      label.includes('credit score')
    ) {
      creditScore = value;
    }

    if (!fullName) {
      if (label.includes('name')) {
        fullName = value;
      } else if (fieldId.endsWith('_title')) {
        fullName = value || lshGetTraderFieldLabel(field);
      }
    }

    if (!id && field?.id) {
      id = String(field.id);
    }
  });

  const resolvedName = lshToText(fullName || fallbackTitle || 'Related party');
  const resolvedAddress = lshBuildRelatedPartyAddress(addressParts);
  if (
    !resolvedName &&
    !idNumber &&
    !phone &&
    !resolvedAddress &&
    !creditScore
  ) {
    return null;
  }

  return {
    id: lshToText(id || resolvedName || `party-${Date.now()}`),
    fullName: resolvedName,
    idNumber: lshToText(idNumber),
    phone: lshToText(phone),
    address: lshToText(resolvedAddress),
    creditScore: lshToText(creditScore),
  };
}

function lshUpsertRelatedParty(
  map: Map<string, ITraderRelatedPartySummary>,
  party?: ITraderRelatedPartySummary | null,
) {
  if (!party) return;

  const key = lshToText(party.id || party.fullName || `party-${map.size + 1}`);
  const existing = map.get(key);

  if (!existing) {
    map.set(key, party);
    return;
  }

  map.set(key, {
    ...existing,
    fullName: party.fullName || existing.fullName,
    idNumber: party.idNumber || existing.idNumber,
    phone: party.phone || existing.phone,
    address: party.address || existing.address,
    creditScore: party.creditScore || existing.creditScore,
  });
}

export function lshExtractRelatedPartiesFromTraderApplication(
  traderApplication?: ITraderApplication,
): ITraderRelatedPartySummary[] {
  const relatedMap = new Map<string, ITraderRelatedPartySummary>();

  const sections = (traderApplication?.forms || [])
    .flatMap((form) => form.sections || [])
    .filter((section) =>
      lshToLower(section?.id).includes('private_company_form_related_parties'),
    );

  sections.forEach((section) => {
    const sectionFields = (section.fields || []) as ITraderFieldLike[];
    const headers = sectionFields.filter((field) => {
      if (String((field as any).__typename || '') !== 'TradingFormText') {
        return false;
      }

      const textType = lshToText((field as any).textType);
      return textType === 'GROUP_HEADER' || Boolean((field as any).groupView);
    });

    if (headers.length > 0) {
      headers.forEach((header, index) => {
        const headerId = lshToText(header.id);
        const headerPrefix = headerId.replace(/_title$/, '');
        const groupedByHeader = sectionFields.filter(
          (field) => lshToText((field as any).groupBy) === headerId,
        );
        const groupedByPrefix = sectionFields.filter((field) => {
          const fieldId = lshToText(field?.id);
          if (!fieldId || fieldId === headerId) return false;
          if (lshToText((field as any).groupBy) === headerId) return false;

          return (
            Boolean(headerPrefix) && fieldId.startsWith(`${headerPrefix}_`)
          );
        });
        const groupedFields = [...groupedByHeader, ...groupedByPrefix];

        lshUpsertRelatedParty(
          relatedMap,
          lshExtractRelatedPartyFromFields(
            groupedFields,
            lshToText((header as any).textTitle) || `Party ${index + 1}`,
          ),
        );
      });
      return;
    }

    const groupMap = new Map<string, ITraderFieldLike[]>();
    sectionFields.forEach((field) => {
      const fieldId = lshToText(field?.id);
      const prefixMatch = fieldId.match(
        /(private_company_form_related_parties_[0-9]+)/,
      );
      if (!prefixMatch) return;

      const key = prefixMatch[1];
      const existing = groupMap.get(key) || [];
      existing.push(field);
      groupMap.set(key, existing);
    });

    if (groupMap.size > 0) {
      Array.from(groupMap.entries()).forEach(([key, fields], index) => {
        lshUpsertRelatedParty(
          relatedMap,
          lshExtractRelatedPartyFromFields(fields, `Party ${index + 1}`),
        );
      });
      return;
    }

    lshUpsertRelatedParty(
      relatedMap,
      lshExtractRelatedPartyFromFields(sectionFields),
    );
  });

  return Array.from(relatedMap.values()).filter(
    (party) =>
      Boolean(party.fullName) ||
      Boolean(party.idNumber) ||
      Boolean(party.phone) ||
      Boolean(party.address) ||
      Boolean(party.creditScore),
  );
}

function lshFindBbbeeField(
  traderApplication?: ITraderApplication,
): ITraderFieldLike | undefined {
  const fields = lshFlattenTraderApplicationFields(traderApplication);
  return fields.find((field) => {
    const id = lshToLower(field?.id);
    const label = lshToLower(lshGetTraderFieldLabel(field));

    if (id.includes('requirements')) return false;
    return (
      id.includes('bbee') ||
      id.includes('bbbee') ||
      label.includes('b-bbee') ||
      label.includes('bbbee')
    );
  });
}

export function lshExtractBbbeeLevelFromTraderApplication(
  traderApplication?: ITraderApplication,
): string {
  const bbbeeField = lshFindBbbeeField(traderApplication);
  if (!bbbeeField) return '';

  return (
    lshGetSingleChoiceOptionTitle(bbbeeField) ||
    lshGetTraderFieldCurrentValue(bbbeeField)
  );
}

function lshCollectBankingSectionFields(
  traderApplication?: ITraderApplication,
): ITraderFieldLike[] {
  if (!traderApplication?.forms) return [];

  return traderApplication.forms.flatMap((form) =>
    (form.sections || [])
      .filter((section) => {
        const sectionId = lshToLower(section?.id);
        const sectionName = lshToLower(section?.name);
        return (
          sectionId.includes('account_details') ||
          sectionId.includes('banking') ||
          sectionName.includes('account details') ||
          sectionName.includes('banking')
        );
      })
      .flatMap((section) =>
        lshFlattenTraderFormFields(
          (section.fields || []) as ITraderFieldLike[],
        ),
      ),
  );
}

function lshFindBankingField(
  fields: ITraderFieldLike[],
  matchers: Array<(field: ITraderFieldLike) => boolean>,
): ITraderFieldLike | undefined {
  return fields.find((field) => matchers.some((matcher) => matcher(field)));
}

export function lshExtractBankingDetailsFromTraderApplication(
  traderApplication?: ITraderApplication,
): ITraderBankingSummary {
  const fields = lshCollectBankingSectionFields(traderApplication);

  const bankField = lshFindBankingField(fields, [
    (field) => lshToLower(field?.id).includes('account_details_bank'),
    (field) => {
      const id = lshToLower(field?.id);
      return (
        id.includes('bank') &&
        !id.includes('branch') &&
        !id.includes('account_type') &&
        !id.includes('account_number')
      );
    },
    (field) => lshToLower(lshGetTraderFieldLabel(field)).includes('bank name'),
  ]);

  const branchField = lshFindBankingField(fields, [
    (field) => lshToLower(field?.id).includes('account_details_branch_code'),
    (field) => lshToLower(field?.id).includes('branch_code'),
    (field) =>
      lshToLower(lshGetTraderFieldLabel(field)).includes('branch code'),
  ]);

  const accountTypeField = lshFindBankingField(fields, [
    (field) => lshToLower(field?.id).includes('account_details_account_type'),
    (field) => lshToLower(field?.id).includes('account_type'),
    (field) =>
      lshToLower(lshGetTraderFieldLabel(field)).includes('account type'),
  ]);

  const accountNumberField = lshFindBankingField(fields, [
    (field) => lshToLower(field?.id).includes('account_details_account_number'),
    (field) => lshToLower(field?.id).includes('account_number'),
    (field) =>
      lshToLower(lshGetTraderFieldLabel(field)).includes('account number'),
    (field) => lshGetTraderFieldValidations(field).includes('BANK_ACCOUNT'),
  ]);

  return {
    bankName: lshGetTraderFieldCurrentValue(bankField),
    branchCode: lshGetTraderFieldCurrentValue(branchField),
    accountType: lshGetSingleChoiceOptionTitle(accountTypeField),
    accountNumber: lshGetTraderFieldCurrentValue(accountNumberField),
  };
}

export function lshResolveEntityTypeTitleFromTraderApplication(
  traderApplication?: ITraderApplication,
  fallback = '',
): string {
  const fields = lshFlattenTraderApplicationFields(traderApplication);
  const field = fields.find((candidate) => {
    const id = lshToLower(candidate.id);
    const label = lshToLower(
      [
        candidate.title,
        candidate.textInputTitle,
        candidate.singleChoiceOptionTitle,
      ]
        .filter(Boolean)
        .join(' '),
    );

    return id.includes('company_type') || label.includes('company type');
  });

  if (!field) return fallback;

  const value = lshGetTraderFieldCurrentValue(field);
  const options = (
    field.options ||
    field.singleChoiceOptionOptions ||
    []
  ).filter(Boolean);
  const selected = options.find(
    (option) => lshToText(option?.value) === lshToText(value),
  );

  return (
    lshToText(selected?.title) || COMPANY_TYPE_MAP[value] || value || fallback
  );
}

function lshNormaliseEntityTypeTitle(title?: string) {
  const value = lshToLower(title).replace(/[\s_-]+/g, ' ');
  if (!value) return 'UNKNOWN';
  if (value.includes('sole')) return 'SOLE_PROPRIETOR';
  if (value.includes('trust')) return 'TRUST';
  if (
    value.includes('co oper') ||
    value.includes('co-oper') ||
    value.includes('cooperative')
  ) {
    return 'CO_OPERATIVE';
  }
  if (value.includes('close')) return 'CLOSE_CORPORATION';
  if (value.includes('public')) return 'PUBLIC_COMPANY';
  if (value.includes('private') || value.includes('pty')) {
    return 'PRIVATE_COMPANY';
  }
  return 'UNKNOWN';
}

export function lshNormaliseEntityRegistrationInput(
  input: string,
  entityTypeTitle?: string,
) {
  const type = lshNormaliseEntityTypeTitle(entityTypeTitle);
  if (type === 'TRUST') {
    return String(input || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  return String(input || '').replace(/\D/g, '');
}

export function lshValidateTraderRegistrationInput({
  entityTypeTitle,
  field,
  value,
}: {
  entityTypeTitle?: string;
  field?: ITraderFieldLike;
  value: string;
}): string | null {
  const validations = lshGetTraderFieldValidations(field);
  const isOptional = Boolean(field?.isOptional);
  const normalized = lshNormaliseEntityRegistrationInput(
    value,
    entityTypeTitle,
  );
  const expectsEntityRegistration =
    validations.length === 0 || validations.includes('ENTITY_REG_NUMBER');

  if (!normalized) {
    return isOptional ? null : 'Required';
  }

  if (!expectsEntityRegistration) return null;

  const type = lshNormaliseEntityTypeTitle(entityTypeTitle);

  if (type === 'PRIVATE_COMPANY') {
    if (!/^\d{12}$/.test(normalized) || !normalized.endsWith('07')) {
      return (
        'Invalid Private Company registration number. Format: ' +
        'YYYY/NNNNNN/07 (e.g., 2024/123456/07). Must end with 07.'
      );
    }
    return null;
  }

  if (type === 'PUBLIC_COMPANY') {
    if (!/^\d{12}$/.test(normalized) || !normalized.endsWith('06')) {
      return (
        'Invalid Public Company registration number. Format: ' +
        'YYYY/NNNNNN/06 (e.g., 2019/004321/06). Must end with 06.'
      );
    }
    return null;
  }

  if (type === 'CLOSE_CORPORATION') {
    if (!/^\d{12}$/.test(normalized) || !normalized.endsWith('23')) {
      return (
        'Invalid Close Corporation registration number. Format: ' +
        'YYYY/NNNNNN/23 (e.g., 2008/012345/23). Must end with 23.'
      );
    }
    return null;
  }

  if (type === 'CO_OPERATIVE') {
    if (!/^\d{12}$/.test(normalized) || !normalized.endsWith('24')) {
      return (
        'Invalid Co Operative registration number. Format: ' +
        'CYYYY/NNNNNN/24 (e.g., C2017/004887/24). Must end with 24.'
      );
    }
    return null;
  }

  if (type === 'TRUST') {
    if (!/^IT\d{4}\d{4}[A-Z]?$/i.test(normalized)) {
      return (
        'Invalid Trust registration number. Format: IT####/YYYY or ' +
        'IT####/YYYYX (e.g., IT1234/2014 or IT1234/2014G).'
      );
    }
    return null;
  }

  if (type === 'SOLE_PROPRIETOR') {
    if (!/^\d{13}$/.test(normalized)) {
      return 'Invalid South African ID number. Format: 1234567890123 (13 digits).';
    }
    return null;
  }

  if (!/^\d{12}$/.test(normalized)) {
    return 'Invalid entity registration number. Expected 12 digits in format YYYY/NNNNNN/XX.';
  }

  return null;
}
